import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { Leaf, MapPin, Eye, Edit, Sprout, GitBranch, Calendar, Building2 } from 'lucide-react';
import type { Cultivar } from '../../types/cultivar';
import ProtectionStatusBadge from './ProtectionStatusBadge';
import PbrMark from './PbrMark';

interface CultivarCardProps {
  cultivar: Cultivar;
  canEdit?: boolean;
}

function CultivarCard({ cultivar, canEdit = false }: CultivarCardProps) {
  const navigate = useNavigate();

  // 'dual' is a real stored role. The old two-way test called it "Scion",
  // which is the one label that is definitely wrong for a cultivar the
  // rootstock pickers are meant to offer.
  const RoleIcon = cultivar.cultivar_role === 'scion' ? Sprout : GitBranch;
  const roleLabel =
    cultivar.cultivar_role === 'rootstock' ? 'Rootstock'
    : cultivar.cultivar_role === 'dual'    ? 'Scion + rootstock'
    :                                        'Scion';
  const breeder = cultivar.breeder_org_name || cultivar.breeder_name || '—';
  // Italic line under the breeder is the scientific name. Prefer botanical_name;
  // fall back to legacy free-text species, but not when it just repeats the crop chip.
  const legacySpecies =
    cultivar.species &&
    cultivar.species.trim().toLowerCase() !== (cultivar.crop_type_name || '').trim().toLowerCase()
      ? cultivar.species
      : null;
  const botanical = cultivar.botanical_name || legacySpecies;

  return (
    <Card className="h-full hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-4 h-full flex flex-col">
        {/* Top row: status badge */}
        <div className="mb-2">
          <ProtectionStatusBadge status={cultivar.protection_status} />
        </div>

        {/* Name + trade name */}
        <div className="mb-2">
          <h3 className="text-base font-bold text-neutral-900 leading-snug line-clamp-2 break-words">
            {cultivar.name}
            <PbrMark cultivar={cultivar} />
          </h3>
          {cultivar.trade_name && (
            <p className="mt-0.5 text-xs text-neutral-500 truncate" title={cultivar.trade_name}>
              Trade: {cultivar.trade_name}
            </p>
          )}
        </div>

        {/* Crop path + role in one line */}
        <div className="mb-3 flex items-center gap-1.5 text-xs text-neutral-600 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-full bg-success-50 text-success-700 px-2 py-0.5 font-medium">
            <Leaf className="h-3 w-3 text-success-500" />
            {cultivar.crop_type_parent_name ? (
              <>
                {cultivar.crop_type_parent_name}
                <span className="opacity-50 mx-0.5">›</span>
                {cultivar.crop_type_name}
              </>
            ) : (
              cultivar.crop_type_name || 'Unknown'
            )}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 px-2 py-0.5 font-medium">
            <RoleIcon className="h-3 w-3 text-neutral-500" />
            {roleLabel}
          </span>
        </div>

        {/* Info rows — single line each, consistent height */}
        <div className="space-y-1 text-sm mb-auto">
          <div className="flex items-center gap-1.5 text-neutral-700 min-w-0" title={breeder}>
            <Building2 className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
            <span className="truncate">{breeder}</span>
          </div>
          {botanical && (
            <div className="flex items-center gap-1.5 text-neutral-700 min-w-0" title={botanical}>
              <Sprout className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
              <span className="italic truncate">{botanical}</span>
            </div>
          )}
          <div className="flex items-center gap-3 text-neutral-500 text-xs">
            {cultivar.origin_country && (
              <span className="inline-flex items-center gap-1 min-w-0">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{cultivar.origin_country}</span>
              </span>
            )}
            {cultivar.year_bred && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {cultivar.year_bred}
              </span>
            )}
          </div>
        </div>

        {/* Actions — always pinned to bottom */}
        <div className="flex items-center gap-2 pt-3 mt-3 border-t border-neutral-100">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/cultivar/${cultivar.id}`)}
            className="flex-1"
          >
            <Eye className="h-4 w-4" />
            View Details
          </Button>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/cultivar/${cultivar.id}/edit`)}
              aria-label="Edit cultivar"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default CultivarCard;
